import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Settings, Clock, Save, Loader2, CreditCard, Smartphone } from 'lucide-react';

export default function SystemSettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [lateToleranceMinutes, setLateToleranceMinutes] = useState(15);
  const [paymentGraceDays, setPaymentGraceDays] = useState(3);
  const [paymentGraceReminderHours, setPaymentGraceReminderHours] = useState(24);
  const [storeUrlAndroid, setStoreUrlAndroid] = useState('');
  const [storeUrlIos, setStoreUrlIos] = useState('');

  useEffect(() => {
    checkPermissions();
    fetchSettings();
  }, [user]);

  const checkPermissions = async () => {
    if (!user) return;

    // Check if user is an owner of any patient
    const { data: links } = await supabase
      .from('patient_user_links')
      .select('role')
      .eq('user_id', user.id)
      .eq('role', 'owner');

    // Also check if user created any patient (original owner)
    const { data: createdPatients } = await supabase
      .from('patients')
      .select('id')
      .eq('created_by', user.id)
      .limit(1);

    setIsOwner((links && links.length > 0) || (createdPatients && createdPatients.length > 0));
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', [
          'late_tolerance_minutes',
          'payment_grace_days',
          'payment_grace_reminder_hours',
          'app_store_url_android',
          'app_store_url_ios',
        ]);

      if (error) {
        console.error('Error fetching settings:', error);
      }

      (data || []).forEach((row) => {
        if (row.key === 'app_store_url_android') {
          setStoreUrlAndroid(typeof row.value === 'string' ? row.value : '');
          return;
        }
        if (row.key === 'app_store_url_ios') {
          setStoreUrlIos(typeof row.value === 'string' ? row.value : '');
          return;
        }
        const num = typeof row.value === 'number' ? row.value : Number(row.value);
        if (!Number.isFinite(num)) return;
        if (row.key === 'late_tolerance_minutes') setLateToleranceMinutes(num || 15);
        if (row.key === 'payment_grace_days') setPaymentGraceDays(num || 3);
        if (row.key === 'payment_grace_reminder_hours') setPaymentGraceReminderHours(num || 24);
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('system_settings')
        .upsert(
          [
            {
              key: 'late_tolerance_minutes',
              value: lateToleranceMinutes,
              description: 'Minutes of tolerance before considering a task as late',
              updated_at: now,
            },
            {
              key: 'payment_grace_days',
              value: paymentGraceDays,
              description: 'Days of tolerance after a failed payment before suspending patients',
              updated_at: now,
            },
            {
              key: 'payment_grace_reminder_hours',
              value: paymentGraceReminderHours,
              description: 'Hours before grace period expiration to send a reminder',
              updated_at: now,
            },
            {
              key: 'app_store_url_android',
              value: storeUrlAndroid.trim() || null,
              description: 'Google Play listing URL for the CareOS Android app',
              updated_at: now,
            },
            {
              key: 'app_store_url_ios',
              value: storeUrlIos.trim() || null,
              description: 'App Store listing URL for the CareOS iOS app',
              updated_at: now,
            },
          ],
          { onConflict: 'key' }
        );

      if (error) throw error;

      toast.success('Configurações salvas com sucesso!');
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error(error.message || 'Erro ao salvar configurações');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <AppLayout title="Configurações" showBack onBack={() => navigate(-1)}>
        <div className="flex items-center justify-center h-64">
          <CareOSLoader size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (!isOwner) {
    return (
      <AppLayout title="Configurações" showBack onBack={() => navigate(-1)}>
        <div className="p-4">
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center">
                  <Settings className="w-8 h-8 text-destructive" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Acesso Restrito</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    As configurações do sistema são acessíveis apenas para responsáveis.
                  </p>
                  <Button onClick={() => navigate('/app')}>
                    Voltar ao Início
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Configurações do Sistema"
      showBack
      onBack={() => navigate(-1)}
    >
      <div className="p-4 space-y-6">
        {/* Late Tolerance Setting */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Tolerância de Atraso
            </CardTitle>
            <CardDescription>
              Define quantos minutos após o horário programado uma tarefa é considerada atrasada.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tolerance">Tempo de tolerância (minutos)</Label>
              <Input
                id="tolerance"
                type="number"
                min={0}
                max={60}
                value={lateToleranceMinutes}
                onChange={(e) => setLateToleranceMinutes(parseInt(e.target.value) || 0)}
                className="w-32"
              />
              <p className="text-xs text-muted-foreground">
                Tarefas serão marcadas como atrasadas {lateToleranceMinutes} minutos após o horário programado.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Billing Grace Period */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Cobrança
            </CardTitle>
            <CardDescription>
              Define a tolerância concedida ao cliente após uma falha de pagamento antes de suspender o acesso.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="grace-days">Dias de tolerância após falha de pagamento</Label>
              <Input
                id="grace-days"
                type="number"
                min={0}
                max={30}
                value={paymentGraceDays}
                onChange={(e) => setPaymentGraceDays(parseInt(e.target.value) || 0)}
                className="w-32"
              />
              <p className="text-xs text-muted-foreground">
                Após uma falha de pagamento, o cliente terá {paymentGraceDays} dia(s) para regularizar antes da suspensão.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="grace-reminder">Lembrete antes do vencimento (horas)</Label>
              <Input
                id="grace-reminder"
                type="number"
                min={1}
                max={72}
                value={paymentGraceReminderHours}
                onChange={(e) => setPaymentGraceReminderHours(parseInt(e.target.value) || 1)}
                className="w-32"
              />
              <p className="text-xs text-muted-foreground">
                Um lembrete será enviado quando faltarem {paymentGraceReminderHours} hora(s) para o fim da tolerância.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* App store links */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              Links das lojas
            </CardTitle>
            <CardDescription>
              Endereços usados pelos convites de download do app no navegador do celular. Campo vazio esconde o convite naquela plataforma.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="store-android">Google Play (Android)</Label>
              <Input
                id="store-android"
                type="url"
                inputMode="url"
                placeholder="https://play.google.com/store/apps/details?id=com.careos.app"
                value={storeUrlAndroid}
                onChange={(e) => setStoreUrlAndroid(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="store-ios">App Store (iOS)</Label>
              <Input
                id="store-ios"
                type="url"
                inputMode="url"
                placeholder="https://apps.apple.com/app/id..."
                value={storeUrlIos}
                onChange={(e) => setStoreUrlIos(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>



        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full"
          size="lg"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Salvar Configurações
            </>
          )}
        </Button>
      </div>
    </AppLayout>
  );
}
